import React from 'react';
import {
  Avatar,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import QuickLinkList, { PublicQuickLink } from './quick-link-list';
import { profileSentence } from './profile-join';
import { GIFTED_STAT_DESCRIPTION } from './gifted-stat';
import RelationshipStatFigures from './relationship-stat-figures';
import { ProfileRelationshipStats } from './relationship-stats';
import { RelationshipKey } from '../home/routes/connections/relationship-nav';

export type { ProfileRelationshipStats };

type ProfileIdentityCardProps = {
  displayName: string;
  channelName: string;
  profileImageURL?: string;
  premiumLabel?: string;
  about?: string;
  links?: PublicQuickLink[];
  stats: ProfileRelationshipStats | null;
  clickZero?: boolean;
  onStat: (list: RelationshipKey) => void;
  actions?: React.ReactNode;
};

function GiftedHelp(): JSX.Element {
  return (
    <Tooltip title={GIFTED_STAT_DESCRIPTION}>
      <IconButton
        size="small"
        aria-label={GIFTED_STAT_DESCRIPTION}
        onClick={(event) => event.stopPropagation()}
      >
        <HelpOutlineIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

function ProfileIdentityCard({
  displayName,
  channelName,
  profileImageURL,
  premiumLabel,
  about,
  links,
  stats,
  clickZero,
  onStat,
  actions,
}: ProfileIdentityCardProps): JSX.Element {
  const statsRow = (below: boolean) => (
    <Stack
      direction="row"
      spacing={2}
      flexWrap="wrap"
      useFlexGap
      sx={{ display: below ? { sm: 'none' } : { xs: 'none', sm: 'flex' } }}
    >
      <RelationshipStatFigures
        stats={stats}
        density="profile"
        clickZero={clickZero !== false}
        onStat={(key) => onStat(key)}
        giftedAccessory={<GiftedHelp />}
      />
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar
          src={profileImageURL}
          alt={displayName}
          sx={{ width: 96, height: 96, flexShrink: 0 }}
        />
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.35rem', sm: '2.125rem' }, lineHeight: 1.2 }}>
            {displayName}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography>@{channelName}</Typography>
            {premiumLabel ? (
              <Chip
                size="small"
                label={premiumLabel}
                sx={{
                  color: 'var(--qc-accent, #2a9dff)',
                  backgroundColor: 'var(--qc-accent-soft, rgba(42, 157, 255, 0.18))',
                  border: '1px solid var(--qc-accent, #2a9dff)',
                  fontWeight: 700,
                }}
              />
            ) : null}
          </Stack>
          {statsRow(false)}
          {actions}
        </Stack>
      </Stack>
      {statsRow(true)}
      <Typography>{profileSentence(about || '', displayName, channelName)}</Typography>
      <QuickLinkList circles links={links || []} channelName={channelName} />
    </Stack>
  );
}

ProfileIdentityCard.defaultProps = {
  profileImageURL: '',
  premiumLabel: '',
  about: '',
  links: [],
  clickZero: true,
  actions: undefined,
};

export default ProfileIdentityCard;
